/**
 * UF-3 (UFC-FOLLOWUP-RESOLUTION-HISTORY) — pure filter + projection
 * for the per-field resolution timeline that lands as a "History"
 * tab on the SED Variable Browser's lineage drawer.
 *
 * Supports two projection inputs:
 *   1. Authoritative SED amendment records (`projectVariableResolutionHistoryFromAmendments`)
 *      loaded by the site/SED amendment route (`GET/POST /api/sites/:siteId/sed-amendments`),
 *      preserving committed lifecycle status (`applied`, `superseded`, `reverted`),
 *      provenance (`source.label`, `source.type`, `source.url`), and `sedVersion` while
 *      excluding uncommitted (`pending`, `rejected`, `accepted`, `approved`) records.
 *   2. Legacy `AuditLogEvent[]` (`projectVariableResolutionHistory`) for explicit
 *      injected `fetchAuditEvents` callers and unit tests.
 */
import type { AuditLogEvent } from '../types/auditLog';
import type { SEDAmendment } from '../types/sedAmendments';
import {
  getSedAmendmentStatusPrecedence,
  isCommittedAmendmentLifecycleStatus,
} from './sed/sedAmendmentMutationCore';

export type ResolutionStrategy =
  | 'applied'
  | 'reverted'
  | 'reapplied-bulk';

export interface VariableResolutionHistoryEntry {
  /** Stable id (amendment id or audit event id). */
  id: string;
  /** When this entry happened — ISO 8601. */
  when: string;
  /** Who resolved (email preferred; uid fallback). */
  actor: string;
  /** Which kind of write this was. */
  strategy: ResolutionStrategy;
  /** Current lifecycle status on the authoritative SED amendment record. */
  lifecycleStatus?: 'applied' | 'superseded' | 'reverted';
  /** Target SED version produced by this committed amendment/revert when recorded. */
  sedVersion?: number;
  /** Source type ('document' | 'expert-judgment' | 'site-visit' | 'external-link' | 'manual_entry'). */
  sourceType?: string;
  /** Pre-write value (provider value for "applied"; current value
   *  for "reverted"). May be undefined for events that pre-date the
   *  before/after stamping. */
  priorValue?: unknown;
  /** Post-write value (amendment newValue for "applied"; restored
   *  provider value for "reverted"). */
  newValue?: unknown;
  /** Free-form reason the user gave when amending. */
  reason?: string;
  /** Source label captured at amend time
   *  (e.g. `'Borehole_Survey.pdf'`). */
  sourceLabel?: string;
  /** External link (when the source kind was `external-link`). */
  sourceUrl?: string;
}

/**
 * Project authoritative SED amendment records for `(sedId, variablePath)`
 * into the resolution history timeline.
 *
 * Only committed lifecycle records (`applied`, `superseded`, `reverted`) are
 * included; uncommitted (`pending`, `rejected`, `accepted`, `approved`) rows
 * are excluded so pending/failed edits never appear as completed resolutions.
 */
export function projectVariableResolutionHistoryFromAmendments(
  amendments: readonly SEDAmendment[] | null | undefined,
  variablePath: string,
  sedId?: string | null,
): VariableResolutionHistoryEntry[] {
  if (!amendments || amendments.length === 0 || !variablePath) return [];

  const dedupedById = new Map<string, SEDAmendment>();
  for (const a of amendments) {
    if (!a || typeof a.id !== 'string' || !a.id.trim()) continue;
    if (a.variablePath !== variablePath) continue;
    if (sedId && a.sedId && a.sedId !== sedId) continue;
    if (!isCommittedAmendmentLifecycleStatus(a.status)) continue;

    const prev = dedupedById.get(a.id);
    if (!prev) {
      dedupedById.set(a.id, a);
    } else {
      const winStatus =
        getSedAmendmentStatusPrecedence(a.status) >= getSedAmendmentStatusPrecedence(prev.status)
          ? a.status
          : prev.status;
      dedupedById.set(a.id, { ...prev, ...a, status: winStatus });
    }
  }

  const out: VariableResolutionHistoryEntry[] = [];
  for (const a of dedupedById.values()) {
    const isRevertRecord =
      Boolean(a.revertedFromAmendmentId) || a.id.startsWith('revert-');
    const strategy: ResolutionStrategy = isRevertRecord
      ? 'reverted'
      : a.reappliedFromAmendmentId
        ? 'reapplied-bulk'
        : 'applied';
    const when = a.appliedAt || a.createdAt || '';
    const actor = a.createdByEmail || a.createdBy || a.appliedBy || 'unknown';
    out.push({
      id: a.id,
      when,
      actor,
      strategy,
      lifecycleStatus: a.status as 'applied' | 'superseded' | 'reverted',
      ...(typeof a.sedVersion === 'number' ? { sedVersion: a.sedVersion } : {}),
      ...(a.source?.type ? { sourceType: a.source.type } : {}),
      priorValue: a.oldValue,
      newValue: a.newValue,
      reason: a.reason || undefined,
      sourceLabel: a.source?.label || undefined,
      sourceUrl: a.source?.url || undefined,
    });
  }

  return out.sort((a, b) => {
    const cmp = (b.when || '').localeCompare(a.when || '');
    if (cmp !== 0) return cmp;
    return (b.sedVersion ?? 0) - (a.sedVersion ?? 0);
  });
}

/**
 * UF-3 — filter the (already-paginated) audit event list to entries
 * for the given variable path, then project them into the
 * timeline shape the drawer renders.
 *
 * Path match is exact (the SED Variable Browser uses the same
 * canonical shape `${domain}.data.${fieldKey}` everywhere). Returns
 * newest-first.
 */
export function projectVariableResolutionHistory(
  events: readonly AuditLogEvent[] | null | undefined,
  variablePath: string,
): VariableResolutionHistoryEntry[] {
  if (!events || events.length === 0 || !variablePath) return [];
  const out: VariableResolutionHistoryEntry[] = [];

  for (const ev of events) {
    if (
      ev.action !== 'sed.amendment_applied'
      && ev.action !== 'sed.amendment_reverted'
      && ev.action !== 'sed.amendments_reapplied'
    ) continue;
    const meta = ev.metadata ?? {};
    const evPath =
      typeof meta.variablePath === 'string'
        ? meta.variablePath
        : undefined;
    if (ev.action === 'sed.amendments_reapplied') continue;
    if (evPath !== variablePath) continue;

    const strategy: ResolutionStrategy =
      ev.action === 'sed.amendment_reverted' ? 'reverted' : 'applied';

    const before = (ev.before ?? {}) as Record<string, unknown>;
    const after = (ev.after ?? {}) as Record<string, unknown>;

    out.push({
      id: ev.id,
      when: ev.when,
      actor: ev.actorEmail || ev.actorUid || 'unknown',
      strategy,
      priorValue: before.value,
      newValue: after.value,
      reason: typeof meta.reason === 'string' ? meta.reason : undefined,
      sourceLabel: typeof meta.sourceLabel === 'string' ? meta.sourceLabel : undefined,
      sourceUrl: typeof meta.sourceUrl === 'string' ? meta.sourceUrl : undefined,
    });
  }

  return out.sort((a, b) => (b.when || '').localeCompare(a.when || ''));
}

/**
 * UF-3 — short label for the timeline row chip. Centralised so the
 * drawer + future surfaces share one vocabulary.
 */
export const RESOLUTION_STRATEGY_LABEL: Record<ResolutionStrategy, string> = {
  applied:         'Applied',
  reverted:        'Reverted',
  'reapplied-bulk': 'Re-applied (bulk)',
};
